// The session window and its transcript, terminal and changes columns, as pictures.
import { COLOR, RADIUS, COMPONENT } from '../tokens.js'
import { INK, DIM, FAINT, ACCENT, MINT, BLUE, CONTROL, CONTROL_HI, SANS, MONO, box, fillBox, capsule, glass, clip, wrap, markdown, paneHeader, capsuleWidth, sessionFrame, actionRow } from './paint.js'

const TRANSCRIPT = [
  ['you', 'Quitting hangs with Docker open. Fix it.'],
  // a markdown reply: the transcript renders lists, quotes, tables and rules as blocks, not as raw text (the
  // table is in `richTranscript`, the full-width Transcript pane, where there is room for it)
  ['md', [
    ['list', ['• Both streams share one pipe', '• Drained before waitUntilExit', '   ◦ 64 KB was the limit']],
    ['quote', 'Quit now takes 0.7 s with a workspace running.'],
  ]],
  // under every reply: Copy (the markdown as written), Change… (prefills the prompt), Open in a Pane (Markdown)
  ['actions'],
]

/// `live`, when there is one, is the connected session as `client.js` keeps it: {title, subtitle, transcript,
/// prompt, status}. Without it the column shows the mock transcript, as it always has.
export function session(c, w, h, live = null) {
  const body = glass(c, w, h, { bar: true })
  c.textBaseline = 'middle'

  // the window's own buttons, top right of the whole thing
  capsule(c, w - 118, 22, 44, 40, '❐', { size: 18 })
  capsule(c, w - 66, 22, 40, 40, '✕', { size: 17 })

  const gap = 22
  const cols = [0.30, 0.38, 0.32]
  const inner = w - gap * 4
  let x = gap
  const xs = cols.map((f) => { const at = x; x += inner * f + gap; return at })
  const ws = cols.map((f) => inner * f)
  const top = 22
  const bottom = body - 20

  // dividers, with the grab handle the app puts on each
  for (let i = 1; i < 3; i++) {
    const dx = xs[i] - gap / 2
    c.strokeStyle = 'rgba(255,255,255,0.14)'
    c.lineWidth = 2
    c.beginPath(); c.moveTo(dx, top + 60); c.lineTo(dx, bottom); c.stroke()
    fillBox(c, dx - 3, body / 2 - 26, 6, 52, 3, 'rgba(255,255,255,0.45)')
  }

  transcriptColumn(c, xs[0], top, ws[0], bottom, live)
  terminalColumn(c, xs[1], top, ws[1], bottom)
  changesColumn(c, xs[2], top, ws[2], bottom)
}

/// The transcript on its own, at a phone's width: the session's title, the one column, the prompt.
export function transcriptPane(c, w, h, live = null) {
  const body = glass(c, w, h, { bar: true })
  c.textBaseline = 'middle'
  transcriptColumn(c, 16, 22, w - 32, body - 20, live)
}

/// The rows of a live transcript, newest last, from the bottom up: only what fits above the prompt is drawn,
/// so a long session shows its tail the way a chat does.
function liveRows(c, live, max) {
  const rows = []
  for (const { who, text } of live.transcript) {
    if (who === 'tool') { rows.push({ who, text, height: 42 }); continue }
    c.font = `400 15px ${SANS}`
    // measure the wrap without drawing: same words, same widths
    let lines = 1, line = ''
    for (const word of text.split(/\s+/)) {
      const next = line ? line + ' ' + word : word
      if (c.measureText(next).width > max && line) { lines++; line = word } else line = next
    }
    rows.push({ who, text, height: 24 + Math.min(lines, 12) * 22 + 14 })
  }
  return rows
}

function transcriptColumn(c, x, top, cw, bottom, live = null) {
  // the session's title lives in the first column
  c.fillStyle = INK
  c.font = `600 21px ${SANS}`
  c.fillText(clip(c, live ? live.title : 'Fix the quit hang', cw - 100), x + 8, top + 20)
  c.fillStyle = DIM
  c.font = `400 14px ${SANS}`
  c.fillText(clip(c, live ? live.subtitle : '/tmp/cody-demo · big-pickle', cw - 100), x + 8, top + 42)
  if (live) {
    // …and each pane has its own duplicate and close
    capsule(c, x + cw - 86, top + 6, 38, 34, '❐', { size: 15 })
    capsule(c, x + cw - 42, top + 6, 36, 34, '✕', { size: 14 })
    paneHeader(c, x + 8, top + 86, '▤  Transcript')
    const status = live.status === 'busy' ? '● Working…' : live.status === 'retry' ? '● Retrying…' : '○ Idle'
    capsule(c, x + cw - 132, top + 82, 124, 32, status, { size: 13, colour: live.status === 'idle' ? DIM : MINT })
    const max = cw - 24
    // the page's own prompt bar sits under the scene; the window paints one only where there is none (VR)
    const ownPrompt = live.ownPrompt !== false
    const promptTop = ownPrompt ? bottom - 100 : bottom - 10
    const rows = liveRows(c, live, max)
    // from the bottom up: keep the rows that fit
    let room = promptTop - 24 - (top + 140)
    let first = rows.length
    while (first > 0 && rows[first - 1].height <= room) { first--; room -= rows[first].height }
    let y = top + 140
    if (rows.length === 0) {
      c.fillStyle = FAINT; c.font = `400 15px ${SANS}`
      c.fillText(live.connecting ? 'Connecting to the Mac…' : live.transcript ? 'Nothing here yet — say something below.' : 'Loading…', x + 8, y + 10)
    }
    for (const row of rows.slice(first)) {
      if (row.who === 'tool') {
        fillBox(c, x + 8, y - 6, max, 32, 10, 'rgba(255,255,255,0.14)')
        c.fillStyle = 'rgba(255,255,255,0.88)'
        c.font = `500 14px ${MONO}`
        c.fillText(clip(c, row.text, max - 20), x + 18, y + 10)
        y += 42
        continue
      }
      const you = row.who === 'you'
      c.fillStyle = you ? ACCENT : row.who === 'thinking' ? FAINT : MINT
      c.font = `700 12px ${SANS}`
      c.fillText(you ? 'YOU' : row.who === 'thinking' ? 'THINKING' : 'CODY', x + 8, y + 2)
      c.fillStyle = you ? INK : row.who === 'thinking' ? DIM : 'rgba(255,255,255,0.82)'
      c.font = `400 15px ${SANS}`
      y = wrap(c, row.text, x + 8, y + 24, max, 22, 12) + 14
    }
    // the prompt, showing what is being typed in the page's box
    if (!ownPrompt) return
    const fieldY = promptTop + 42
    fillBox(c, x + 8, fieldY, max, 44, 22, 'rgba(255,255,255,0.16)')
    c.fillStyle = live.prompt ? INK : FAINT
    c.font = `400 15px ${SANS}`
    c.fillText(clip(c, live.prompt || 'Message the agent', max - 100), x + 24, fieldY + 22)
    capsule(c, x + 8 + max - 38, fieldY + 6, 32, 32, '↑', { size: 16, weight: 700 })
    return
  }
  // …and each pane has its own duplicate and close
  capsule(c, x + cw - 86, top + 6, 38, 34, '❐', { size: 15 })
  capsule(c, x + cw - 42, top + 6, 36, 34, '✕', { size: 14 })

  paneHeader(c, x + 8, top + 86, '▤  Transcript')
  // the pane menu's Render Markdown switch, on: the reply below is blocks, not the raw text
  capsule(c, x + cw - 132, top + 82, 124, 32, '✓ Markdown', { size: 13 })

  let y = top + 140
  const max = cw - 24
  for (const [who, text] of TRANSCRIPT) {
    if (y > bottom - 140) break
    if (who === 'md') {
      y = markdown(c, text, x + 8, y, max)
      continue
    }
    if (who === 'actions') {
      y = actionRow(c, x + 8, y)
      continue
    }
    if (who === 'tool') {
      fillBox(c, x + 8, y - 6, max, 32, 10, 'rgba(255,255,255,0.14)')
      c.fillStyle = 'rgba(255,255,255,0.88)'
      c.font = `500 14px ${MONO}`
      c.fillText(clip(c, text, max - 20), x + 18, y + 10)
      y += 42
      continue
    }
    c.fillStyle = who === 'you' ? ACCENT : MINT
    c.font = `700 12px ${SANS}`
    c.fillText(who === 'you' ? 'YOU' : 'CODY', x + 8, y + 2)
    c.fillStyle = who === 'you' ? INK : 'rgba(255,255,255,0.82)'
    c.font = `400 15px ${SANS}`
    y = wrap(c, text, x + 8, y + 24, max, 22, 6) + 14
  }

  // the prompt: mode, attach, the Mac's clipboard (one press pastes it; long press sends the prompt the other
  // way — SessionWindow.swift) and commands in a row, then the field with mic and send inside it
  const promptTop = bottom - 100
  const mode = capsuleWidth(c, '⚡ Auto', 14) + 28
  capsule(c, x + 8, promptTop, mode, 32, '⚡ Auto', { fill: COLOR.green, colour: COLOR.greenInk, size: 14, weight: 700 })
  capsule(c, x + 8 + mode + 8, promptTop, 36, 32, '⛓', { size: 14 })
  capsule(c, x + 8 + mode + 52, promptTop, 36, 32, '📋', { size: 13 })
  capsule(c, x + 8 + mode + 96, promptTop, 36, 32, '⌘', { size: 14 })
  const fieldY = promptTop + 42
  fillBox(c, x + 8, fieldY, max, 44, 22, 'rgba(255,255,255,0.16)')
  c.fillStyle = FAINT
  c.font = `400 15px ${SANS}`
  c.fillText(clip(c, 'Message the agent, /command or !shell', max - 100), x + 24, fieldY + 22)
  capsule(c, x + 8 + max - 76, fieldY + 6, 32, 32, '🎤', { size: 13 })
  capsule(c, x + 8 + max - 38, fieldY + 6, 32, 32, '↑', { size: 16, weight: 700 })
}

const SHELL = [
  ['p', 'cody@studio cody-demo % ', 'swift test --filter VirtualKeyboard'],
  ['o', "Test Suite 'VirtualKeyboardTests' started"],
  ['ok', 'strikes land on the key under the fingertip'],
  ['ok', 'a 3 m/s glitch is not a keystroke'],
  ['ok', 'the model learns a drifting hand'],
  ['o', 'Executed 3 tests, with 0 failures (0.041s)'],
  ['p', 'cody@studio cody-demo % ', 'git status --short'],
  ['m', ' M hosts/macos/HarnessHost/HostModel.swift'],
  ['p', 'cody@studio cody-demo % ', '▊'],
]

function terminalColumn(c, x, top, cw, bottom) {
  paneHeader(c, x + 8, top + 30, '⌗  Terminal')
  // the tab row: the open shells, the active one in blue, then New and the keyboard controls
  let tx = x + 8
  const tab = capsuleWidth(c, '⌗ zsh 1', 14) + 30
  capsule(c, tx, top + 62, tab, 34, '⌗ zsh 1', { fill: BLUE, size: 14, weight: 600 }); tx += tab + 8
  const plus = capsuleWidth(c, '＋ New', 14) + 30
  capsule(c, tx, top + 62, plus, 34, '＋ New', { size: 14 }); tx += plus + 8
  const hand = capsuleWidth(c, '☝ Hand keyboard', 14) + 30
  if (tx + hand < x + cw) { capsule(c, tx, top + 62, hand, 34, '☝ Hand keyboard', { size: 14 }); tx += hand + 8 }
  const keys = capsuleWidth(c, '⌨ U.S. keys', 14) + 30
  if (tx + keys < x + cw + 30) capsule(c, tx, top + 62, Math.min(keys, x + cw - tx), 34, '⌨ U.S. keys', { fill: COLOR.controlOn, colour: COLOR.inkOnLight, size: 14, weight: 600 })

  // the terminal itself: near black, rounded, filling the column — leaving room for the keypad under it
  const pad = COMPONENT.keypad
  const padHeight = pad.keyHeight * 2 + pad.gap * 2 + pad.chipHeight + 12
  const ty = top + 110
  const th = bottom - ty - padHeight - 12
  c.save()
  box(c, x + 8, ty, cw - 16, th, RADIUS.terminal); c.clip()
  fillBox(c, x + 8, ty, cw - 16, th, RADIUS.terminal, COLOR.terminal)
  c.font = `400 14px ${MONO}`
  let line = ty + 24
  for (const [kind, a, b] of SHELL) {
    if (line > ty + th - 10) break
    let at = x + 20
    if (kind === 'p') {
      c.fillStyle = 'rgba(255,255,255,0.85)'; c.fillText(a, at, line); at += c.measureText(a).width
      c.fillStyle = INK; c.fillText(b, at, line)
    } else if (kind === 'ok') {
      c.fillStyle = MINT; c.fillText('✓', at, line)
      c.fillStyle = 'rgba(255,255,255,0.72)'; c.fillText(clip(c, a, cw - 60), at + 18, line)
    } else {
      c.fillStyle = kind === 'm' ? ACCENT : 'rgba(255,255,255,0.72)'
      c.fillText(clip(c, a, cw - 40), at, line)
    }
    line += 21
  }
  c.restore()

  // the keypad, toggled with Keys: two rows of big keys and the snippets as chips (COMPONENT.keypad)
  const py = bottom - padHeight
  fillBox(c, x + 8, py - 8, cw - 16, padHeight + 8, RADIUS.terminal, 'rgba(0,0,0,0.001)')
  const rows = [['esc', 'tab', '⇧tab', '^C', '^D', '^Z', '^L', '^R'], ['home', '↑', 'end', '←', '↓', '→', '⌫', '|', 'return']]
  rows.forEach((row, r) => {
    let kx = x + 8
    for (const key of row) {
      const kw = key === 'return' ? Math.min(pad.wideKeyWidth, x + cw - 8 - kx) : Math.min(pad.keyWidth, (cw - 16 - pad.gap * (row.length - 1)) / row.length)
      if (kx + kw > x + cw - 8) break
      capsule(c, kx, py + r * (pad.keyHeight + pad.gap), kw, pad.keyHeight, key, { size: 15, mono: true })
      kx += kw + pad.gap
    }
  })
  let sx = x + 8
  for (const chip of ['git status', 'git log', 'Run tests', '✎ Edit Snippets']) {
    const w = capsuleWidth(c, chip, 13) + 24
    if (sx + w > x + cw - 8) break
    capsule(c, sx, py + 2 * (pad.keyHeight + pad.gap), w, pad.chipHeight, chip, { size: 13 })
    sx += w + pad.gap
  }
}

function changesColumn(c, x, top, cw, bottom) {
  paneHeader(c, x + 8, top + 30, '± Changes')
  // "vs base branch | Uncommitted" — a segmented capsule, the chosen half white
  const segW = cw - 16
  fillBox(c, x + 8, top + 62, segW, 34, 17, CONTROL)
  fillBox(c, x + 8 + segW / 2, top + 62, segW / 2, 34, 17, COLOR.controlOn)
  c.textAlign = 'center'
  c.fillStyle = INK; c.font = `500 14px ${SANS}`; c.fillText('vs base branch', x + 8 + segW / 4, top + 80)
  c.fillStyle = COLOR.inkOnLight; c.font = `600 14px ${SANS}`; c.fillText('Uncommitted', x + 8 + (3 * segW) / 4, top + 80)
  c.textAlign = 'left'

  c.fillStyle = INK
  c.font = `600 17px ${SANS}`
  c.fillText('2 files  +28 −9', x + 8, top + 122)

  // the files, the chosen one highlighted
  const files = [['HostModel.swift', 'hosts/macos/HarnessHost', '+28', '−9', true], ['DockerRuntime.swift', 'hosts/macos/HarnessHost', '+22', '−1', false]]
  let y = top + 158
  for (const [name, dir, add, del, chosen] of files) {
    if (chosen) fillBox(c, x + 8, y - 18, cw - 16, 50, 11, 'rgba(255,255,255,0.18)')
    c.fillStyle = INK; c.font = `600 14px ${MONO}`; c.fillText(clip(c, name, cw - 100), x + 18, y - 2)
    c.fillStyle = FAINT; c.font = `400 11px ${SANS}`; c.fillText(clip(c, dir, cw - 130), x + 18, y + 16)
    c.textAlign = 'right'
    c.fillStyle = 'rgba(255,255,255,0.7)'; c.font = `400 12px ${SANS}`; c.fillText('added', x + cw - 76, y + 16)
    c.fillStyle = MINT; c.font = `500 12px ${MONO}`; c.fillText(add, x + cw - 40, y + 16)
    c.fillStyle = COLOR.red; c.fillText(del, x + cw - 14, y + 16)
    c.textAlign = 'left'
    y += 58
  }

  // the diff below, as the app draws it
  let ly = y + 6
  c.font = `400 13px ${MONO}`
  for (const [kind, text] of DIFF) {
    if (ly > bottom - 12) break
    const fill = kind === '@' ? COLOR.diffHunk : kind === '+' ? COLOR.diffAdded : kind === '-' ? COLOR.diffRemoved : null
    if (fill) fillBox(c, x + 8, ly - 11, cw - 16, 22, 4, fill)
    c.fillStyle = kind === '@' ? 'rgba(255,255,255,0.75)' : INK
    c.fillText(kind === '@' ? '' : kind, x + 16, ly)
    c.fillText(clip(c, text, cw - 44), x + 30, ly)
    ly += 23
  }
}

const DIFF = [
  ['@', '@@ -894,12 +894,22 @@'],
  ['-', 'process.standardError = Pipe()'],
  ['+', 'process.standardError = pipe'],
  ['+', 'process.standardInput = .nullDevice'],
  [' ', 'try process.run()'],
]

/// A ```ui block in a reply, rendered the way the headset renders it since 09-25 (RichInterface.swift): a form
/// laid out like Settings — rounded groups of rows with the system insets, label left and control right; a
/// segmented picker for a short choice, a menu for a long one; a plain text field inside its row; a slider, a
/// stepper, a date; section headers and notes; Submit and Cancel. Right: what the agent wrote to get it.
export function richTranscript(c, w, h) {
  const body = sessionFrame(c, w, h, '▤  Transcript')
  const left = 34
  const colW = Math.round(w * 0.52)

  c.fillStyle = ACCENT; c.font = `700 12px ${SANS}`
  c.fillText('YOU', left, 146)
  c.fillStyle = INK; c.font = `400 15px ${SANS}`
  c.fillText('Stream a window from one of my Macs.', left, 168)
  c.fillStyle = MINT; c.font = `700 12px ${SANS}`
  c.fillText('CODY', left, 200)
  c.fillStyle = 'rgba(255,255,255,0.82)'; c.font = `400 15px ${SANS}`
  c.fillText('Which one, and how? Fill this in and I will start it.', left, 222)

  // the form: a title, then groups of rows
  let y = 252
  c.fillStyle = INK; c.font = `600 18px ${SANS}`
  c.fillText('Stream a Mac window', left + 6, y)
  y += 22
  const rowH = 44
  const group = (rows) => {
    const gh = rows.length * rowH
    fillBox(c, left, y, colW, gh, 12, 'rgba(255,255,255,0.10)')
    rows.forEach((draw, i) => {
      const ry = y + i * rowH
      if (i > 0) fillBox(c, left + 16, ry, colW - 16, 1, 0, 'rgba(255,255,255,0.10)')
      draw(ry + rowH / 2)
    })
    y += gh + 14
  }
  const label = (text, cy) => { c.fillStyle = INK; c.font = `400 15px ${SANS}`; c.textBaseline = 'middle'; c.fillText(text, left + 16, cy) }
  const right = left + colW - 16
  const segmented = (options, on, cy) => {
    const cw = 62, total = cw * options.length
    fillBox(c, right - total, cy - 15, total, 30, 8, 'rgba(0,0,0,0.22)')
    options.forEach((o, i) => {
      const x = right - total + i * cw
      if (o === on) fillBox(c, x + 2, cy - 13, cw - 4, 26, 6, CONTROL_HI)
      c.fillStyle = o === on ? INK : DIM; c.font = `${o === on ? 600 : 500} 13px ${SANS}`; c.textAlign = 'center'; c.fillText(o, x + cw / 2, cy); c.textAlign = 'left'
    })
  }
  const menu = (value, cy) => { const mw = capsuleWidth(c, value + '  ⌃⌄', 13) + 24; capsule(c, right - mw, cy - 15, mw, 30, value + '  ⌃⌄', { size: 13 }) }
  const toggle = (on, cy) => { fillBox(c, right - 52, cy - 15, 52, 30, 15, on ? COLOR.green : 'rgba(255,255,255,0.22)'); c.fillStyle = INK; c.beginPath(); c.arc(on ? right - 15 : right - 37, cy, 12, 0, Math.PI * 2); c.fill() }
  const field = (placeholder, cy) => { c.fillStyle = FAINT; c.font = `400 15px ${SANS}`; c.textAlign = 'right'; c.fillText(placeholder, right, cy); c.textAlign = 'left' }
  const slider = (frac, value, cy) => {
    const sw = 150, sx = right - sw - 44
    fillBox(c, sx, cy - 2, sw, 4, 2, 'rgba(255,255,255,0.25)'); fillBox(c, sx, cy - 2, sw * frac, 4, 2, BLUE)
    c.fillStyle = '#fff'; c.beginPath(); c.arc(sx + sw * frac, cy, 9, 0, Math.PI * 2); c.fill()
    c.fillStyle = DIM; c.font = `400 13px ${SANS}`; c.textAlign = 'right'; c.fillText(value, right, cy); c.textAlign = 'left'
  }
  const stepper = (value, cy) => { c.fillStyle = INK; c.font = `400 15px ${SANS}`; c.textAlign = 'right'; c.fillText(value, right - 84, cy); c.textAlign = 'left'; fillBox(c, right - 72, cy - 14, 72, 28, 8, 'rgba(0,0,0,0.22)'); c.fillStyle = INK; c.font = `600 15px ${SANS}`; c.textAlign = 'center'; c.fillText('−', right - 54, cy); c.fillText('+', right - 18, cy); fillBox(c, right - 36, cy - 8, 1, 16, 0, 'rgba(255,255,255,0.2)'); c.textAlign = 'left' }
  const date = (value, cy) => { const dw = capsuleWidth(c, value, 13) + 24; fillBox(c, right - dw, cy - 15, dw, 30, 8, 'rgba(0,0,0,0.22)'); c.fillStyle = INK; c.font = `400 13px ${SANS}`; c.textAlign = 'center'; c.fillText(value, right - dw / 2, cy); c.textAlign = 'left' }

  group([
    (cy) => { label('Which Mac?', cy); segmented(['Studio', 'Air', 'Mini'], 'Studio', cy) },
    (cy) => { label('Frame rate', cy); menu('60 fps', cy) },
    (cy) => { label('Stream sound', cy); toggle(true, cy) },
  ])
  c.fillStyle = FAINT; c.font = `600 12px ${SANS}`; c.textBaseline = 'middle'; c.fillText('QUALITY', left + 16, y + 2); y += 18
  group([
    (cy) => { label('Sharpness', cy); slider(0.7, '70', cy) },
    (cy) => { label('Windows at once', cy); stepper('2', cy) },
    (cy) => { label('Until', cy); date('Oct 3, 2026', cy) },
  ])
  group([
    (cy) => { label('Anything else', cy); field('Optional', cy) },
  ])
  c.fillStyle = DIM; c.font = `400 13px ${SANS}`
  wrap(c, 'A note the agent wrote under the form, as a note field.', left + 6, y - 4, colW - 12, 19)
  y += 26
  capsule(c, left + colW - 208, y, 96, 36, '↑ Start', { fill: BLUE, colour: INK, size: 14, weight: 700 })
  capsule(c, left + colW - 104, y, 104, 36, 'Not now', { size: 14 })
  c.textBaseline = 'middle'
  // and under the reply, the same three actions as under any other
  actionRow(c, left, body - 58)

  // what the agent wrote: the fenced block, so the format is on the page
  const rx = left + colW + 30
  c.fillStyle = FAINT; c.font = `600 13px ${SANS}`
  c.fillText('WHAT THE AGENT WROTE', rx, 150)
  fillBox(c, rx, 166, w - rx - 34, MARKDOWN_JSON.length * 21 + 24, 12, 'rgba(0,0,0,0.28)')
  c.fillStyle = 'rgba(255,255,255,0.82)'; c.font = `400 13px ${MONO}`
  MARKDOWN_JSON.forEach((line, i) => c.fillText(line, rx + 14, 190 + i * 21))
  c.fillStyle = DIM; c.font = `400 14px ${SANS}`
  wrap(c, 'Submit posts the answers as the next prompt in this same chat — "Which Mac?: Studio", one line per field — never a throwaway session; Cancel sends its label. Field types: text, multiline, choice, multi, toggle, slider, stepper, date, section, note. A {"action": "newSession"} block opens a session window with its prompt instead. JSON the app does not understand stays a code block.', rx, 166 + MARKDOWN_JSON.length * 21 + 52, w - rx - 34, 21)
}
